import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import isPropValid from '@emotion/is-prop-valid'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import Stack from '@mui/material/Stack'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import Divider from '@mui/material/Divider'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import LogoutIcon from '@mui/icons-material/Logout'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import NewspaperIcon from '@mui/icons-material/Newspaper'
import MenuIcon from '@mui/icons-material/Menu'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import TranslateIcon from '@mui/icons-material/Translate'
import { styled, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'

import { useCurrentUserQuery } from '../../../services/user'
import { UserLanguages } from '../../../types'
import logo from '../../../../assets/sfctcv-logo-white.png'

export const HEADER_HEIGHT = '55px'

const Wrapper = styled('header')(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'row',
  height: HEADER_HEIGHT,
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: theme.shadows[4],
  zIndex: 5,
  position: 'fixed',
  left: 0,
  width: '100%',
  gap: '1rem',
}))
const Logo = styled('img')(() => ({
  height: '100%',
  width: 'auto',
}))
const SafeArea = styled('div')({
  minHeight: HEADER_HEIGHT,
})
const HeaderButton = styled(Button, {
  shouldForwardProp: prop => typeof prop === 'string' && isPropValid(prop) && prop !== 'active',
})<{ active: boolean }>(({ active }) => ({
  color: 'white',
  borderRadius: 100,
  padding: '10px 20px',
  backgroundColor: active ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
  border: '0.15rem solid transparent',
  '&:hover': {
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: '0.15rem solid rgba(255, 255, 255, 0.5)',
  },
}))

function ProfileMenu() {
  const navigate = useNavigate()
  const { data } = useCurrentUserQuery()
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.down('md'))

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const language = i18n.resolvedLanguage as UserLanguages

  return (
    <>
      <IconButton
        aria-label="account"
        size="large"
        onClick={handleClick}
        sx={{ height: '45px', width: '45px' }}
      >
        <AccountCircleIcon htmlColor="white" fontSize="large" />
      </IconButton>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'account-menu-button',
        }}
        anchorOrigin={
          mobileView
            ? {
                vertical: 'top',
                horizontal: 'right',
              }
            : undefined
        }
        transformOrigin={
          mobileView
            ? {
                vertical: 'bottom',
                horizontal: 'right',
              }
            : undefined
        }
      >
        <MenuItem
          aria-readonly
          onClick={() => {
            handleClose()
            navigate('/profile')
          }}
        >
          {data?.first_name} {data?.last_name}
        </MenuItem>
        <MenuItem>
          <ListItemIcon>
            <TranslateIcon fontSize="small" />
          </ListItemIcon>
          <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }} size="small">
            <Select
              labelId="language-select-label"
              id="language-select"
              value={language}
              onChange={event => {
                void i18n.changeLanguage(event.target.value)
              }}
            >
              <MenuItem value={'en'}>{t('en')}</MenuItem>
              <MenuItem value={'fr'}>{t('fr')}</MenuItem>
            </Select>
          </FormControl>
        </MenuItem>
        {/* Add line to show appli version */}
        <MenuItem disabled>
          {t('Version')} {APP_VERSION}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleClose()
            navigate('/logout')
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t('Se déconnecter')}
        </MenuItem>
      </Menu>
    </>
  )
}

function HeaderMenu({ isMobile }: { isMobile?: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: currentUserData } = useCurrentUserQuery()

  const isStaff = !!currentUserData?.is_staff

  const Item = isMobile ? MenuItem : HeaderButton

  return (
    <Stack flex="1 0" gap="1rem" direction={isMobile ? 'column' : 'row'}>
      <Item
        variant={isMobile ? 'outlined' : 'text'}
        startIcon={<CalendarMonthIcon />}
        onClick={() => {
          navigate('/dashboard')
        }}
        active={
          (window.location.pathname.includes('/dashboard') ||
            window.location.pathname.includes('/events')) &&
          !window.location.pathname.includes('/admin')
        }
      >
        {isMobile && (
          <ListItemIcon>
            <CalendarMonthIcon />
          </ListItemIcon>
        )}
        {t('Événements')}
      </Item>

      <Item
        variant="text"
        startIcon={<ConfirmationNumberIcon />}
        onClick={() => {
          navigate('/inscriptions')
        }}
        active={
          window.location.pathname.includes('/inscriptions') &&
          !window.location.pathname.includes('/admin')
        }
      >
        {isMobile && (
          <ListItemIcon>
            <ConfirmationNumberIcon />
          </ListItemIcon>
        )}
        {t('Mes inscriptions')}
      </Item>

      <Item
        variant="text"
        startIcon={<WorkspacePremiumIcon />}
        onClick={() => {
          navigate('/certificates')
        }}
        active={
          window.location.pathname.includes('/certificates') &&
          !window.location.pathname.includes('/admin')
        }
      >
        {isMobile && (
          <ListItemIcon>
            <WorkspacePremiumIcon />
          </ListItemIcon>
        )}
        {t('Mes attestations')}
      </Item>

      {isStaff ? (
        <Item
          variant="text"
          startIcon={<NewspaperIcon />}
          onClick={() => {
            navigate('/abstracts')
          }}
          active={
            window.location.pathname.includes('/abstracts') &&
            !window.location.pathname.includes('/admin')
          }
        >
          {isMobile && (
            <ListItemIcon>
              <NewspaperIcon />
            </ListItemIcon>
          )}
          {t('Résumés')}
        </Item>
      ) : null}

      {isStaff ? (
        <Item
          variant="text"
          startIcon={<AdminPanelSettingsIcon />}
          onClick={() => {
            navigate('/admin')
          }}
          active={window.location.pathname.includes('/admin')}
        >
          {isMobile && (
            <ListItemIcon>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
          )}
          {t('Administration')}
        </Item>
      ) : null}
    </Stack>
  )
}

function MobileHeaderMenu() {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <HeaderButton variant="text" startIcon={<MenuIcon />} onClick={handleClick} active={open}>
        {t('Menu')}
      </HeaderButton>

      <Menu
        id="header-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        MenuListProps={{
          'aria-labelledby': 'menu-button',
        }}
      >
        <HeaderMenu isMobile />
      </Menu>
    </>
  )
}

export default function Header() {
  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <>
      <Wrapper
        sx={{
          top: mobileView ? 'auto' : 0,
          bottom: mobileView ? 0 : 'auto',
          padding: mobileView ? '0 5px' : '0 30px',
        }}
      >
        <Link to="dashboard" style={{ height: '90%' }}>
          <Logo src={logo} alt="SFCTCV" />
        </Link>

        {!mobileView && <HeaderMenu />}

        <Stack direction="row" alignItems="center" gap="0.5rem">
          <ProfileMenu />
          {mobileView && <MobileHeaderMenu />}
        </Stack>
      </Wrapper>
      <SafeArea />
    </>
  )
}
